type ReleaseFn = () => void;

export type AcquireOptions = {
  signal?: AbortSignal;
};

/**
 * Simple in-memory concurrency limiter for media fetches.
 *
 * Why: `AuthenticatedImage` fetches blobs in JS, so it can easily start hundreds
 * of parallel requests when a large grid mounts. That can overwhelm the backend
 * (and its upstream object storage sockets), leading to "forever spinners".
 */
class ConcurrencyLimiter {
  private readonly max: number;
  private active = 0;
  private readonly queue: Array<{
    resolve: (release: ReleaseFn) => void;
    reject: (err: Error) => void;
    signal?: AbortSignal;
  }> = [];

  constructor(max: number) {
    this.max = max;
  }

  getStats(): { max: number; active: number; queued: number } {
    return { max: this.max, active: this.active, queued: this.queue.length };
  }

  acquire(options: AcquireOptions = {}): Promise<ReleaseFn> {
    if (options.signal?.aborted) {
      return Promise.reject(new DOMException("Aborted", "AbortError") as unknown as Error);
    }

    return new Promise<ReleaseFn>((resolve, reject) => {
      const entry = { resolve, reject, signal: options.signal };

      const tryDequeue = () => {
        if (this.active >= this.max) return;

        // Skip aborted waiters.
        while (this.queue.length > 0) {
          const next = this.queue.shift()!;
          if (next.signal?.aborted) {
            next.reject(new DOMException("Aborted", "AbortError") as unknown as Error);
            continue;
          }
          this.active += 1;
          next.resolve(() => this.release());
          return;
        }
      };

      if (this.active < this.max) {
        this.active += 1;
        resolve(() => this.release());
        return;
      }

      this.queue.push(entry);

      if (options.signal) {
        const onAbort = () => {
          // Remove from queue if still waiting.
          const idx = this.queue.indexOf(entry);
          if (idx !== -1) {
            this.queue.splice(idx, 1);
            reject(new DOMException("Aborted", "AbortError") as unknown as Error);
          }
          options.signal?.removeEventListener("abort", onAbort);
        };
        options.signal.addEventListener("abort", onAbort, { once: true });
      }

      // In case capacity becomes available before any future release,
      // attempt to dequeue now.
      tryDequeue();
    });
  }

  private release() {
    this.active = Math.max(0, this.active - 1);

    // Drain queue in FIFO order.
    while (this.active < this.max && this.queue.length > 0) {
      const next = this.queue.shift()!;
      if (next.signal?.aborted) {
        next.reject(new DOMException("Aborted", "AbortError") as unknown as Error);
        continue;
      }
      this.active += 1;
      next.resolve(() => this.release());
      break;
    }
  }
}

// Conservative default: enough parallelism for a smooth grid, but avoids stampedes.
const DEFAULT_MEDIA_FETCH_CONCURRENCY = 12;

export const mediaFetchLimiter = new ConcurrencyLimiter(DEFAULT_MEDIA_FETCH_CONCURRENCY);

